import { useState, useEffect } from 'react';
import { ArrowLeft, Swords, Shield } from 'lucide-react';
import NicknameBar from '../components/NicknameBar';
import OrnamentSeparator from '../components/OrnamentSeparator';
import { getTeamButtonClass, TEAM_BORDER_COLOR } from '../components/teamTheme';

interface VotingState {
  isActive: boolean;
  voteId: string | null;
  canVote: boolean;
  hasVoted: boolean;
  votedForSheetId: string | null;
}

interface CharacterSheetViewProps {
  characterId: string; // Actually a sheetId now
  onBack: () => void;
  votingState?: VotingState;
  onVoteComplete?: (sheetId: string) => void;
}

interface Sheet {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  bonuses: Array<{ attribute: string; value: number }>;
  maluses: Array<{ attribute: string; value: number }>;
  moves: Array<{ name: string; description: string }>;
  alignment?: string;
  notes?: string;
  damage?: number;
  armor?: number;
  maxHP?: number;
  level?: number;
}

/**
 * Character Sheet View
 *
 * Visualizza la scheda completa di un personaggio.
 * Può essere interrotta da eventi interattivi (votazioni, challenge).
 */
function CharacterSheetView({ characterId: sheetId, onBack, votingState, onVoteComplete }: CharacterSheetViewProps) {
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVoting, setIsVoting] = useState(false);

  // Get user's team from localStorage for styling
  const userTeam = (localStorage.getItem('teamColor') || 'BLUE').toUpperCase();
  const isVotingActive = votingState?.isActive && votingState?.canVote;
  const hasVoted = votingState?.hasVoted || false;

  useEffect(() => {
    loadSheet();
  }, [sheetId]);

  const loadSheet = async () => {
    try {
      const response = await fetch(`/api/sheets/${sheetId}`);
      if (!response.ok) throw new Error('Failed to load character');

      const data = await response.json();
      setSheet(data.sheet);
      setLoading(false);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const teamBorder = TEAM_BORDER_COLOR[userTeam] || 'border-parchment-300';
  const teamBtnClass = getTeamButtonClass(userTeam);

  const handleVote = async () => {
    if (!votingState?.voteId || hasVoted || isVoting) return;

    setIsVoting(true);

    try {
      const audienceId = localStorage.getItem('audienceId');
      if (!audienceId) {
        alert('Errore: ID audience non trovato. Ricarica la pagina.');
        setIsVoting(false);
        return;
      }

      const response = await fetch(`/api/sheet-votes/${votingState.voteId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audienceId,
          optionType: 'sheet',
          sheetId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore durante il voto');
      }

      // Notifica il parent che il voto è stato completato
      onVoteComplete?.(sheetId);
      // Torna indietro dopo il voto con successo
      setTimeout(() => onBack(), 500);
    } catch (err: any) {
      alert(`Errore: ${err.message}`);
      setIsVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-parchment-400 mx-auto mb-4"></div>
          <p className="text-ink-light text-lg font-heading">Caricamento personaggio...</p>
        </div>
      </div>
    );
  }

  if (error || !sheet) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-team-red text-lg mb-4">{error || 'Personaggio non trovato'}</p>
          <button
            onClick={onBack}
            className="btn-fantasy"
          >
            Indietro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col pt-8">
      <NicknameBar />
      {/* Header fisso con pulsante indietro ben visibile */}
      <div className="flex-shrink-0 sticky top-8 z-40 bg-parchment-100/95 backdrop-blur-sm border-b border-parchment-300 px-4 py-3">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-ink font-heading font-semibold text-lg active:scale-95 transition-transform"
        >
          <ArrowLeft size={24} strokeWidth={2.5} />
          Tutti gli eroi
        </button>
      </div>

      {/* Contenuto scrollabile */}
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        <div className="max-w-3xl mx-auto">
          {/* Character Header */}
          <div className={`parchment-card p-4 border-l-4 ${teamBorder} mb-4`}>
            <div className="flex items-start gap-4">
              {sheet.imageUrl && (
                <img
                  src={sheet.imageUrl}
                  alt={sheet.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-parchment-300"
                />
              )}
              <div className="flex-1">
                <h1 className="text-2xl font-display font-bold text-ink mb-1">{sheet.name}</h1>
                <p className="text-ink-light text-base font-body">{sheet.description}</p>
              </div>
            </div>
          </div>

          {/* Combat Stats */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="parchment-panel text-center py-2">
              <p className="text-xs text-ink-light uppercase">Danno</p>
              <p className="text-xl font-display font-bold text-ink">{sheet.damage ?? 0}</p>
            </div>
            <div className="parchment-panel text-center py-2">
              <p className="text-xs text-ink-light uppercase">Armatura</p>
              <p className="text-xl font-display font-bold text-ink">{sheet.armor ?? 0}</p>
            </div>
            <div className="parchment-panel text-center py-2">
              <p className="text-xs text-ink-light uppercase">PF Max</p>
              <p className="text-xl font-display font-bold text-ink">{sheet.maxHP ?? 10}</p>
            </div>
            <div className="parchment-panel text-center py-2">
              <p className="text-xs text-ink-light uppercase">Livello</p>
              <p className="text-xl font-display font-bold text-ink">{sheet.level ?? 1}</p>
            </div>
          </div>

          <OrnamentSeparator className="my-3" />

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {/* Bonuses */}
            {sheet.bonuses && sheet.bonuses.length > 0 && (
              <div className="parchment-panel border-l-4 border-team-green/40">
                <h3 className="text-lg font-heading font-semibold text-team-green mb-2 flex items-center gap-2">
                  <Shield size={18} />
                  Bonus
                </h3>
                <ul className="space-y-1">
                  {sheet.bonuses.map((bonus, idx) => (
                    <li key={idx} className="text-ink-light text-base flex justify-between font-body">
                      <span>{bonus.attribute}</span>
                      <span className="text-team-green font-bold">+{bonus.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Maluses */}
            {sheet.maluses && sheet.maluses.length > 0 && (
              <div className="parchment-panel border-l-4 border-team-red/40">
                <h3 className="text-lg font-heading font-semibold text-team-red mb-2 flex items-center gap-2">
                  <Swords size={18} />
                  Malus
                </h3>
                <ul className="space-y-1">
                  {sheet.maluses.map((malus, idx) => (
                    <li key={idx} className="text-ink-light text-base flex justify-between font-body">
                      <span>{malus.attribute}</span>
                      <span className="text-team-red font-bold">{malus.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Alignment */}
          {sheet.alignment && (
            <div className="parchment-panel border-l-4 border-team-yellow/40 mb-4">
              <h3 className="text-lg font-heading font-semibold text-team-yellow mb-1">Allineamento</h3>
              <p className="text-ink text-base font-body">{sheet.alignment}</p>
            </div>
          )}

          {/* Moves */}
          {sheet.moves && sheet.moves.length > 0 && (
            <div className="parchment-panel mb-4">
              <h3 className="text-lg font-heading font-semibold text-ink mb-2">Mosse</h3>
              <div className="space-y-2">
                {sheet.moves.map((move, idx) => (
                  <div key={idx} className="bg-parchment-100/60 rounded-lg p-3 border border-parchment-300">
                    <h4 className="font-heading font-semibold text-ink text-base mb-1">{move.name}</h4>
                    <p className="text-ink-light text-sm font-body">{move.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {sheet.notes && (
            <div className="parchment-panel mb-4">
              <h3 className="text-lg font-heading font-semibold text-ink-light mb-2">Note</h3>
              <p className="text-ink-light text-base whitespace-pre-wrap font-body">{sheet.notes}</p>
            </div>
          )}

          {/* Pulsante vota - subito dopo il contenuto */}
          {isVotingActive && !hasVoted && (
            <button
              onClick={handleVote}
              disabled={isVoting}
              className={`w-full py-4 rounded-xl transition text-xl mt-4 ${teamBtnClass} ${isVoting ? 'opacity-70' : ''}`}
            >
              {isVoting ? 'Invio...' : `Vota ${sheet.name}`}
            </button>
          )}

          {/* Conferma voto */}
          {hasVoted && (
            <div className="w-full bg-team-green text-parchment-50 py-4 font-heading font-bold rounded-xl text-center text-xl mt-4">
              &#10003; Votato!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CharacterSheetView;
